import { useTranslation } from "react-i18next";
import AuthLayout from "components/AuthLayout";
import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import Input from "components/Input";
import InfoIcon from "assets/icons/error.svg?react";
import Button from "components/Button";
import workspaceService from "api/workspaceService";
import { ESteps } from "components/Stepper";
import { Context, Hub } from "react-signalr/src/signalr/types";
import OtpInput from "react-otp-input";
import { User, UserManager } from "oidc-client-ts";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router-dom";
import HollowButton from "components/HollowButton";
import GmailIcon from "assets/icons/gmail.svg?react";
import OutlookIcon from "assets/icons/outlook.svg?react";
import AppleIcon from "assets/icons/apple.svg?react";
import AEmail from "assets/icons/aemail.svg?react";
import NotaIcon from "assets/icons/nota.svg?react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { validateEmail } from "utils/isEmail";
import { validatePasswordStrength } from "utils/validatePasswordStrength";
import classNames from "classnames";
import { getErrorMessage } from "utils/getErrorMessage";
import ReactGA from "react-ga4";
import { useMediaQuery } from "usehooks-ts";

const RegisterModule = ({
  SignalRContext,
}: {
  SignalRContext: Context<Hub<string, string>>;
}) => {
  const { t } = useTranslation();

  const steps = [
    {
      type: ESteps.INFORMATION,
      title: t("activation.stepper.yourData.title"),
      text: t("activation.stepper.yourData.description"),
    },
    {
      type: ESteps.EMAIL_VERIFICATION,
      title: t("activation.stepper.emailVerification.title"),
      text: t("activation.stepper.emailVerification.description"),
    },
    {
      type: ESteps.ID_VERIFICATION,
      title: t("identity.verifyTitle"),
      text: t("activation.stepper.identity.description"),
    },
    {
      type: ESteps.KEY_GENERATION,
      title: t("activation.stepper.recoveryKey.title"),
      text: t("activation.stepper.recoveryKey.description"),
    },
  ];

  const { linkToken, linkCode } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordWeak, setPasswordWeak] = useState(false);
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [step, setStep] = useState<ESteps>(ESteps.INFORMATION);
  const [approveRules, setApproveRules] = useState(false);
  const [allowBack, setAllowBack] = useState(false);
  const [error, setError] = useState<string>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const { mutate: register, isLoading: isRegistering } =
    workspaceService.useRegisterInWorkspace();
  const {
    mutate: verify,
    isLoading: isVerifingCode,
    isError: codeIncorrect,
  } = workspaceService.useVerifyEmail();

  SignalRContext.useSignalREffect("verified", async () => {}, []);

  const handleCreate = () => {
    setError(null);

    sendGaEvent("reg_create", "Utwórz konto", {});

    register(
      {
        AcceptNotaRules: approveRules,
        AcceptRules: approveRules,
        Email: email,
        FirstName: name,
        LastName: lastName,
        Password: password,
        RepeatPassword: rePassword,
        ConnectionId: SignalRContext.connection?.connectionId ?? "",
        LegalPerson: false,
      },
      {
        onSuccess: (resp) => {
          if (resp.data.VerifyEmail) {
            setToken(resp.data.EmailToken);
            setStep(ESteps.EMAIL_VERIFICATION);
          }

          sendGaEvent("reg_create_sucess", "Utworzno konto", {});
        },
        onError: (resp) => {
          const msg = getErrorMessage(resp);

          if (msg) {
            setError(msg);
          }

          sendGaEvent("reg_create_error", "Błąd tworzenia konta", {});
        },
      },
    );
  };
  const auth = useAuth();
  const userManager = new UserManager(auth.settings);
  const setUser = (accessToken: string, refreshToken: string) => {
    userManager.storeUser(
      new User({
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: "Bearer",
        profile: { sub: "", iss: "", aud: "", exp: 0, iat: 0 },
      }),
    );
  };
  const [chooseRegisterMethod, setChooseRegisterMethod] = useState(true);
  const [chooseRegisterMethodError, setChooseRegisterMethodError] =
    useState(false);

  const handleCode = () => {
    sendGaEvent("reg_verifyemail", "Weryfikacja adresu e-mail", {});
    verify(
      {
        Token: linkToken ?? token,
        Code: linkCode ?? otp,
      },
      {
        onSuccess: (resp) => {
          sendGaEvent(
            "reg_verifyemail_success",
            "Poprawna weryfikacja adresu e-mail",
            {},
          );

          setUser(resp.data.AccessToken, resp.data.RefreshToken);
          auth.signinSilent();
          navigate("/activation");
        },
      },
    );
  };

  const navigateToMainPage = () => {
    navigate("/home");
  };

  const navigateToLogin = () => {
    auth.signinRedirect();
  };

  const sendGaEvent = (action: string, label: string, params?: any) => {
    try {
      ReactGA.event(
        {
          category: "registration",
          action: action,
          label: label, // optional
          transport: "xhr", // optional, beacon/xhr/image
        },
        params,
      );
    } catch { /* ignore GA errors */ }
  };

  useEffect(() => {
    if (!linkCode || !linkToken) return;
    handleCode();
  }, [linkCode]);

  useEffect(() => {
    setPasswordMismatch(password && rePassword && password !== rePassword);
    setPasswordWeak(password && !validatePasswordStrength(password));
  }, [password, rePassword]);

  useEffect(() => {
    if (otp && otp.length === 6) {
      handleCode();
    }
  }, [otp]);

  const checkCan = () => {
    if (isRegistering) return true;

    if (!validateEmail(email ?? "")) {
      return true;
    }

    if (
      !password.length ||
      !rePassword.length ||
      !name.length ||
      !lastName.length
    ) {
      return true;
    }

    if (password !== rePassword) {
      return true;
    }

    if (!validatePasswordStrength(password)) {
      return true;
    }

    if (!approveRules) {
      return true;
    }

    return false;
  };

  const back = () => {
    if (step === ESteps.INFORMATION) {
      if (!chooseRegisterMethod) {
        setChooseRegisterMethod(true);
        setChooseRegisterMethodError(false);
        setAllowBack(false);
      }
    } else if (step === ESteps.EMAIL_VERIFICATION) {
      setStep(ESteps.INFORMATION);
      setChooseRegisterMethod(false);
    }
  };

  useEffect(() => {
    if (approveRules) {
      setChooseRegisterMethodError(false);
    }
  }, [approveRules]);

  const AcceptRulesRegion = () => {
    return (
      <div className={styles.checkbox}>
        <FormControl required error={chooseRegisterMethodError}>
          <FormGroup>
            <FormControlLabel
              required
              control={
                <Checkbox
                  checked={approveRules}
                  onChange={(e) => setApproveRules(e.target.checked)}
                />
              }
              label={
                <span className={styles.rules}>
                  {t("activation.termsAccept")}
                </span>
              }
            ></FormControlLabel>
            {chooseRegisterMethodError && (
              <div className={styles.errorBlock}>
                {t("validation.acceptTermsRequired")}
              </div>
            )}
          </FormGroup>
        </FormControl>
      </div>
    );
  };

  const ChooseMethod = () => {
    return (
      <>
        <div className={styles.externalLogin}>
          <div
            onClick={() => {
              if (!approveRules) {
                setChooseRegisterMethodError(true);
                return;
              }
              setChooseRegisterMethod(false);
              setAllowBack(true);
            }}
            className={styles.external}
          >
            <AEmail></AEmail> {t("auth.emailOrPassword")}
          </div>
          <a
            className={styles.external}
            onClick={() => {
              if (!approveRules) {
                setChooseRegisterMethodError(true);
                return;
              }
              auth.signinRedirect({
                acr_values: "idp:Google",
              });
            }}
          >
            <GmailIcon></GmailIcon> {t("activation.registerWithGoogle")}
          </a>
          <a
            className={styles.external}
            onClick={() => {
              if (!approveRules) {
                setChooseRegisterMethodError(true);
                return;
              }
              auth.signinRedirect({
                acr_values: "idp:Microsoft",
              });
            }}
          >
            <OutlookIcon></OutlookIcon> {t("activation.registerWithMicrosoft")}
          </a>
          <a
            className={styles.external}
            onClick={() => {
              if (!approveRules) {
                setChooseRegisterMethodError(true);
                return;
              }
              auth.signinRedirect({
                acr_values: "idp:Apple",
              });
            }}
          >
            <AppleIcon></AppleIcon> {t("activation.registerWithApple")}
          </a>
        </div>
        <AcceptRulesRegion></AcceptRulesRegion>
      </>
    );
  };

  return (
    <AuthLayout
      actualStep={step}
      steps={steps}
      footerChildren={
        <div className={styles.footerLeft}>
          <HollowButton
            text={t("common.backHome")}
            onClick={() => navigateToMainPage?.()}
          ></HollowButton>
          <HollowButton
            text={t("common.login")}
            onClick={() => navigateToLogin?.()}
          ></HollowButton>
        </div>
      }
    >
      <>
        {allowBack && (
          <div className={styles.backTop}>
            {" "}
            <HollowButton
              text={t("common.backArrow")}
              onClick={() => back?.()}
            ></HollowButton>
          </div>
        )}
        {step === ESteps.INFORMATION && (
          <>
            <div className={styles.title}>{t("activation.createAccount")}</div>
            <div className={styles.side}>{t("activation.startUsing")} </div>
            {chooseRegisterMethod && <ChooseMethod></ChooseMethod>}
            {!chooseRegisterMethod && (
              <form>
                <div className={styles.inputs}>
                  <Input
                    id="firstName"
                    className={styles.input}
                    disabledTags
                    label={t("forms.firstName")}
                    value={name}
                    onChange={(v) => setName(v)}
                  />
                  <Input
                    id="lastName"
                    className={styles.input}
                    label={t("forms.lastName")}
                    disabledTags
                    value={lastName}
                    onChange={(v) => setLastName(v)}
                  />
                  <Input
                    className={styles.input}
                    id="username"
                    label={t("forms.email")}
                    disabledTags
                    value={email}
                    onChange={(v) => {
                      setEmail(v);
                    }}
                  />
                  <div className={styles.box}>
                    <Input
                      className={styles.input}
                      id="new-password"
                      autoComplete="new-password"
                      label={t("auth.password")}
                      type="password"
                      disabledTags
                      value={password}
                      onChange={(v) => setPassword(v)}
                      allowToShowPassword={true}
                    />
                  </div>
                  <div className={styles.box}>
                    <Input
                      className={styles.input}
                      id="confirm-password"
                      autoComplete="new-password"
                      label={t("auth.repeatPassword")}
                      disabledTags
                      type="password"
                      value={rePassword}
                      onChange={(v) => setRePassword(v)}
                      allowToShowPassword={true}
                      RightIcon={
                        passwordMismatch && (
                          <InfoIcon
                            data-tooltip-id={`bar-1`}
                            data-tooltip-content={t("validation.passwordMismatch")}
                            title={t("validation.passwordMismatch")}
                            className={styles.error}
                          />
                        )
                      }
                    />
                  </div>

                  {passwordWeak && (
                    <div className={styles.errorBlock}>
                      {t("validation.weakPassword")}
                    </div>
                  )}

                  <div className={styles.hint}>
                    <i>
                      {t("validation.passwordRequirements")}
                    </i>
                  </div>

                  {passwordMismatch && (
                    <div className={styles.errorBlock}>
                      {t("validation.passwordsDoNotMatch")}
                    </div>
                  )}
                  {error && <div className={styles.errorBlock}>{error}</div>}
                </div>
                <div className={styles.button}>
                  <Button
                    className={styles.b}
                    text={t("common.next")}
                    disabled={checkCan()}
                    onClick={() => handleCreate()}
                    loading={isRegistering}
                  />
                </div>
                <div className={styles.hint}>
                  {t("activation.hasAccount")} <a href="/login">{t("common.signIn")}</a>
                </div>
              </form>
            )}
          </>
        )}
        {step === ESteps.EMAIL_VERIFICATION && (
          <>
            <div className={styles.title}>{t("activation.confirmEmail")}</div>
            <div className={styles.side}>
              {t("activation.confirmEmailInfo")}
            </div>

            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              inputStyle={styles.otp}
              containerStyle={styles.cont}
              renderSeparator={<span></span>}
              renderInput={(props) => <input {...props} />}
              shouldAutoFocus={true}
            />

            {codeIncorrect && (
              <div
                className={classNames(styles.errorBlock, styles.marginBottom)}
              >
                {t("validation.invalidCode")}
              </div>
            )}

            <Button
              className={styles.button}
              onClick={handleCode}
              text={t("common.next")}
              disabled={isVerifingCode}
              loading={isVerifingCode}
            />

            <div className={styles.hint}>
              {t("activation.resendCodeInfo")}{" "}
              <a href="#" onClick={() => handleCreate()}>
                {t("activation.resendCode")}
              </a>
            </div>
          </>
        )}

        {isMobile && (
          <div className={styles.ourlogo}>
            <NotaIcon></NotaIcon>
          </div>
        )}
      </>
    </AuthLayout>
  );
};

export default RegisterModule;
